import { Icon } from "@blueprintjs/core";
import {
  differenceInDays,
  differenceInHours,
  formatDistanceToNow,
  isAfter,
} from "date-fns";
import { Link } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { Box } from "@/components/layout/flexbox";
import { GrantExtensionInfoComponent } from "@/components/pages_logged_in/program/components/GrantExtensionInfoComponent";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { ComponentStatus, ComponentTypeEnum, ProgramStatus } from "@/graphql";
import {
  MainComponent,
  ParticipantComponent,
  ResponseComponent,
  StatisticComponent,
} from "@/types/programs";
import { formatDate } from "@/utils/datetime";

import { AnnouncementUpcomingInfo } from "./AnnouncementUpcomingInfo";

export interface ComponentMetadata {
  title: string;
  description: string;
  componentTitle: string;
  guidanceParticipantDesc: string;
  linkDesc: string;
  launchDateDesc: string;
  dueDateDesc: string;
  closeDateDesc: string;
  participantEligibilityDesc: string;
  isSurvey: boolean;
  isActionItems: boolean;
  isAnnouncement: boolean;
  isAIRoleplay: boolean;
  isVideo: boolean;
  isArticle: boolean;
  isGroupMeeting: boolean;
  isActionItemCompatible: boolean;
  visibleActionsItems?: boolean | null;
  hasParticipantGuidanceAndDueDate: boolean;
  componentTypeName: string;
  upcomingInfo: React.ReactNode;
  analyticsTitle: string;
  isEligible: boolean;
  invalidDependency: string | null;
  dependentInfo: React.ReactNode;
  status: string;
  isOneOneMaster: boolean;
  ongoingInfo: React.ReactNode;
  isOneOneMeetingIntro: boolean;
  isOneOneSession: boolean;
  completedInfo: React.ReactNode;
}

const COMPONENT_TYPE_TO_NAME: Partial<Record<ComponentTypeEnum, string>> = {
  [ComponentTypeEnum.Survey]: "Survey",
  [ComponentTypeEnum.ActionItem]: "Action Item",
  [ComponentTypeEnum.Announcement]: "Announcement",
  [ComponentTypeEnum.AiRoleplay]: "Roleplay",
  [ComponentTypeEnum.Video]: "Video",
  [ComponentTypeEnum.Article]: "Article",
  [ComponentTypeEnum.GroupMeeting]: "Group Meeting",
  [ComponentTypeEnum.OneOneCoachingMaster]: "1:1 coaching",
};

type Component = Omit<
  MainComponent | ResponseComponent | StatisticComponent,
  "requiredSendLaunchEmail"
> & {
  participantSubmitAt?: string | null;
  isParticipantRoleplay?: boolean | null;
  targetDueDate?: string | null;
  targetEndDate?: string | null;
  prerequisiteComponent?: MainComponent["prerequisiteComponent"];
  programId?: string | null;
  participantPrelaunchVisibility?: boolean | null;
  requiredSendLaunchEmail?: boolean | null;
  visibleUpcomingQuestions?: boolean | null;
  forAllParticipants?: boolean | null;
  grantedCloseDate?: string | null;
  grantedCloseDateUserIds?: Array<number> | null;
};

interface BuildComponentMetadataProps {
  component?: Component;
  isProgramLead?: boolean;
  initialType?: ComponentTypeEnum | null;
}

export function buildComponentMetadata({
  component,
  isProgramLead = false,
  initialType,
}: BuildComponentMetadataProps): ComponentMetadata {
  const {
    targetStartDate,
    targetDueDate,
    targetEndDate,
    type: componentType,
    status,
    prerequisiteComponent,
    visibleUpcomingQuestions,
    participantPrelaunchVisibility,
    requiredSendLaunchEmail,
    grantedCloseDate,
    grantedCloseDateUserIds,
  } = component || { status: "InDesign" };

  const type = componentType || initialType;
  const isSurvey = type === ComponentTypeEnum.Survey;
  const isActionItems = type === ComponentTypeEnum.ActionItem;
  const isAnnouncement = type === ComponentTypeEnum.Announcement;
  const isAIRoleplay = type === ComponentTypeEnum.AiRoleplay;
  const isVideo = type === ComponentTypeEnum.Video;
  const isArticle = type === ComponentTypeEnum.Article;
  const isGroupMeeting = type === ComponentTypeEnum.GroupMeeting;
  const isActionItemCompatible = isActionItems || isArticle || isVideo;
  const isOneOneMaster = type === ComponentTypeEnum.OneOneCoachingMaster;
  const isOneOneMeetingIntro = type === ComponentTypeEnum.OneOneCoachingIntro;
  const isOneOneSession = type === ComponentTypeEnum.OneOneCoachingSession;

  const visibleActionsItems =
    (isActionItems || isArticle || isVideo) &&
    (isProgramLead
      ? ["Upcoming", "InDesign"].includes(status)
      : status !== "Upcoming" ||
        (status === "Upcoming" && visibleUpcomingQuestions));

  const hasParticipantGuidanceAndDueDate =
    isSurvey || isActionItems || isVideo || isArticle || isAIRoleplay;

  const { isEligible, isShowDependentInfo } = getEligibleData(
    component?.prerequisiteComponent,
  );

  const dependentInfo = !isProgramLead ? (
    <Box my={2}>
      <Icon icon="info-sign" style={{ marginRight: 8 }} />
      Please finish{" "}
      <Link
        to={`/programs/${component?.programId}#component_id_${prerequisiteComponent?.id}`}
      >
        {prerequisiteComponent?.name}
      </Link>{" "}
      before moving on to this component
    </Box>
  ) : (
    <Box my={2}>
      <Icon icon="info-sign" style={{ marginRight: 8 }} />
      Participant are required to complete{" "}
      <Link
        to={`/programs/${component?.programId}#component_id_${prerequisiteComponent?.id}`}
      >
        {prerequisiteComponent?.name}
      </Link>{" "}
    </Box>
  );

  const prerequisiteStartDate = prerequisiteComponent?.targetStartDate;

  const dateDiff =
    (prerequisiteComponent &&
      targetEndDate &&
      prerequisiteStartDate &&
      differenceInHours(
        new Date(prerequisiteStartDate),
        new Date(targetEndDate),
      )) ||
    0;

  const invalidDependency =
    dateDiff > 0 && !!prerequisiteComponent
      ? `The end date ${formatDate(targetEndDate, "MMM d")} must be at least 12 hours later than the start date of "${
          prerequisiteComponent?.name
        }" ${formatDate(prerequisiteStartDate, "MMM d")}`
      : null;

  const getComponentTitle = () => {
    switch (true) {
      case isAnnouncement:
        return "Title";
      case isSurvey:
        return "Survey Name";
      case isGroupMeeting:
        return "Meeting Name";
      default:
        return "Component Name";
    }
  };

  const getTitle = () => {
    switch (true) {
      case isAnnouncement:
        return "Let’s configure your announcement";
      case isSurvey:
        return "Lastly, let’s configure the survey";
      case isOneOneMaster:
        return "Basic Details";
      default:
        return "Lastly, let’s configure the component";
    }
  };

  const getLaunchDateDesc = () => {
    switch (true) {
      case isAnnouncement:
        return "When the announcement will become prominent to participants.";
      case isSurvey:
        return "When participants can first take the survey. You can adjust this later.";
      case isOneOneMaster:
        return "Participants can start scheduling sessions from this date onward.";
      case isActionItems:
        return "When participants will first be prompted to start the action items.";
      case isVideo:
        return "When participants will first be prompted to start the video.";
      case isArticle:
        return "When participants will first be prompted to start the article.";
      case isAIRoleplay:
        return "When participants will first be prompted to start the roleplay.";
      case isGroupMeeting:
        return "";
      default:
        return "When participants will first be prompted to start the component.";
    }
  };

  const getDueDateDesc = () => {
    switch (true) {
      case isAnnouncement || isGroupMeeting || isOneOneMaster:
        return "";
      case isSurvey:
        return "Set the deadline for when participants should complete the survey. This is the date that will be communicated. You can adjust this later.";
      case isActionItems:
        return "Set the deadline for when participants should complete the action items.";
      case isVideo:
        return "Set the deadline for when participants should complete the video.";
      case isArticle:
        return "Set the deadline for when participants should complete the article.";
      case isAIRoleplay:
        return "Set the deadline for when participants should complete the roleplay.";
      default:
        return "Set the deadline for when participants should complete the component.";
    }
  };

  const getCloseDateDesc = () => {
    switch (true) {
      case isAnnouncement:
        return "After this date, the announcement will no longer be prominently displayed to participants.";
      case isSurvey:
        return "Choose the final cut-off date, after which no new submissions will be accepted. You can adjust this later.";
      case isOneOneMaster:
        return "Last day for scheduling or conducting sessions.";
      case isActionItems:
        return "After this date participants will no longer be able to mark action items as complete.";
      case isVideo:
        return "After this date participants will no longer be able to complete the video.";
      case isArticle:
        return "After this date participants will no longer be able to complete the article.";
      case isAIRoleplay:
        return "After this date participants will no longer be able to complete the roleplay.";
      case isGroupMeeting:
        return "";
      default:
        return "After this date participants will no longer be able to complete the component.";
    }
  };

  const getUpcomingInfo = () => {
    switch (true) {
      case isAnnouncement:
        return (
          <AnnouncementUpcomingInfo
            targetStartDate={targetStartDate}
            participantPrelaunchVisibility={participantPrelaunchVisibility}
            requiredSendLaunchEmail={requiredSendLaunchEmail}
            isAdmin={isProgramLead}
          />
        );
      case isSurvey:
        return (
          <>
            💡 Participants will be able to take the survey starting{" "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM d" />
            </b>
          </>
        );
      case isOneOneMaster:
        return (
          <>
            <Icon icon="time" style={{ marginRight: 10, marginBottom: 2 }} />
            {isProgramLead
              ? "Participants will be able match with a coach and schedule sessions starting "
              : "You will be able to start scheduling sessions on "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM d" />
            </b>
          </>
        );
      case isOneOneMeetingIntro:
        return (
          <>
            <Icon icon="time" style={{ marginRight: 10, marginBottom: 2 }} />
            {isProgramLead
              ? "Participants will be able match with a coach starting"
              : "You will be able match with a coach starting "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM dd" />
            </b>
          </>
        );

      case isGroupMeeting:
        return (
          <>
            💡 This meeting starts{" "}
            {targetStartDate
              ? formatDistanceToNow(new Date(targetStartDate), {
                  addSuffix: true,
                })
              : "soon"}
          </>
        );
      case isArticle:
        return (
          <>
            💡 Participants will be prompted to complete this starting{" "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM d" />
            </b>
          </>
        );
      case isVideo:
        return (
          <>
            💡 Participants will be prompted to complete this starting{" "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM d" />
            </b>
          </>
        );
      case isAIRoleplay:
        return (
          <>
            💡 Participants will be prompted to complete this roleplay starting{" "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM d" />
            </b>
          </>
        );
      case isActionItems:
        return (
          <>
            💡 Participants will be prompted to complete these tasks starting{" "}
            <b>
              <DateTimeDisplay time={targetStartDate} formatString="MMM d" />
            </b>
          </>
        );
      default:
        return null;
    }
  };

  const currentTime = new Date();
  const daysUntilClose = targetEndDate
    ? differenceInDays(new Date(targetEndDate), currentTime)
    : 0;

  const isCloseToday = daysUntilClose === 0;

  const isPastDue = targetDueDate
    ? isAfter(currentTime, new Date(targetDueDate))
    : false;

  const isParticipantHasDependentInfo = isShowDependentInfo && !isProgramLead;

  const leadDependentInfo =
    isShowDependentInfo && isProgramLead ? <Box>{dependentInfo}</Box> : null;
  const hasGrantExtensionInfo =
    component && isProgramLead && hasGrantExtension(componentType, component);
  const grantExtensionInfo = hasGrantExtensionInfo ? (
    <GrantExtensionInfoComponent
      grantedCloseDate={grantedCloseDate}
      grantedCloseDateUserIds={grantedCloseDateUserIds}
    />
  ) : null;

  const daysUntilCloseText = pluralize({
    amount: daysUntilClose,
    singular: "more day",
    plural: "more days",
  });

  const getOngoingInfo = () => {
    switch (true) {
      case isParticipantHasDependentInfo:
        return (
          <>
            {dependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isCloseToday:
        return (
          <>
            💡 Participants have until today to complete this component.
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isPastDue:
        return (
          <>
            💡 This component is now due. Participants can still complete it for{" "}
            {daysUntilCloseText}
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isOneOneMaster:
        return isProgramLead ? (
          <>
            <Icon icon="time" style={{ marginRight: 10, marginBottom: 2 }} />
            Participants will be able to schedule and conduct sessions until{" "}
            <b>
              <DateTimeDisplay time={targetEndDate} formatString="MMM d" />
            </b>
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        ) : null;
      case isGroupMeeting:
        return <>💡 This meeting is happening now</>;
      case isArticle:
        return (
          <>
            💡 Participants can complete this for {daysUntilCloseText}
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isVideo:
        return (
          <>
            💡 Participants can complete this for {daysUntilCloseText}
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isAIRoleplay:
        return (
          <>
            💡 Participants can complete this roleplay for {daysUntilCloseText}
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isActionItems:
        return (
          <>
            💡 Participants can complete these action items for{" "}
            {daysUntilCloseText}
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      case isSurvey:
        return (
          <>
            💡 Participants can take this survey for {daysUntilCloseText}
            {leadDependentInfo}
            {grantExtensionInfo}
          </>
        );
      // isAnnouncement will cover by logic return body in Announcement Component
      default:
        return null;
    }
  };

  const getCompletedInfo = () => {
    if (hasGrantExtensionInfo) {
      return grantExtensionInfo;
    }

    return null;
  };

  const getAnalyticsTitle = () => {
    switch (true) {
      case isSurvey:
        return "Question Analytics";
      case isAIRoleplay:
        return "Performance Per Scenario";
      case isOneOneMaster:
        return "Progress Per Session";
      default:
        return "Completion Rates Per Action Item";
    }
  };

  return {
    title: getTitle(),
    description: isAnnouncement
      ? "Enter a title for your announcement"
      : isSurvey
        ? 'This will be visible to participants. For example, "Pre-Program Survey".'
        : "This will be visible to participants",
    componentTitle: getComponentTitle(),
    guidanceParticipantDesc: isSurvey
      ? "This will be displayed to participants in the user interface before they begin their survey."
      : "This will be visible to participants",
    linkDesc: isArticle
      ? "Link to the content you want participants to read"
      : "Link to the content you want participants to watch. We will embed this on the page.",
    launchDateDesc: getLaunchDateDesc(),
    dueDateDesc: getDueDateDesc(),
    closeDateDesc: getCloseDateDesc(),
    participantEligibilityDesc:
      "Determine when participants become eligible to start this component.",
    isSurvey,
    isActionItems,
    isAnnouncement,
    isAIRoleplay,
    isVideo,
    isArticle,
    isGroupMeeting,
    isActionItemCompatible,
    isOneOneMaster,
    visibleActionsItems,
    hasParticipantGuidanceAndDueDate,
    componentTypeName: (type && COMPONENT_TYPE_TO_NAME[type]) || "Component",
    upcomingInfo: getUpcomingInfo(),
    ongoingInfo: getOngoingInfo(),
    completedInfo: getCompletedInfo(),
    analyticsTitle: getAnalyticsTitle(),
    isEligible,
    invalidDependency,
    dependentInfo: isShowDependentInfo ? dependentInfo : null,
    status: status || "InDesign",
    isOneOneMeetingIntro,
    isOneOneSession,
  };
}

export const getEligibleData = (
  component: ParticipantComponent["prerequisiteComponent"],
) => {
  const isEligible = !component || !!component.participantSubmitAt;
  return {
    isEligible,
    isShowDependentInfo: !isEligible && !!component,
  };
};

export function isComponentInProgressOrOverdue(
  componentStatus: ComponentStatus | undefined,
): boolean {
  if (!componentStatus) return false;
  return (
    componentStatus === ComponentStatus.Ongoing ||
    componentStatus === ComponentStatus.PastDue
  );
}

export function isComponentUpcomingOrSoon(
  componentStatus: ComponentStatus | undefined,
): boolean {
  if (!componentStatus) return false;
  return (
    componentStatus === ComponentStatus.Upcoming ||
    componentStatus === ComponentStatus.Soon
  );
}

export function isComponentCompleted(
  componentStatus: ComponentStatus | undefined,
): boolean {
  if (!componentStatus) return false;
  return componentStatus === ComponentStatus.Completed;
}

export function canEndEarlyComponent(
  componentStatus: ComponentStatus | undefined,
  isGroupMeeting: boolean,
  isLead: boolean,
): boolean {
  if (!isLead) return false;
  return componentStatus === ComponentStatus.Ongoing && !isGroupMeeting;
}

export function hasGrantExtension(
  componentType: ComponentTypeEnum | undefined,
  component: Component,
): boolean {
  if (!component) return false;
  if (!component.grantedCloseDate || !component.grantedCloseDateUserIds)
    return false;
  return (
    componentType !== ComponentTypeEnum.GroupMeeting &&
    componentType !== ComponentTypeEnum.Announcement &&
    component.grantedCloseDate !== null &&
    component.grantedCloseDateUserIds.length > 0
  );
}

export function canGrantExtension(
  component: {
    status: ComponentStatus;
    type: ComponentTypeEnum;
  },
  isLead: boolean,
  programStatus?: ProgramStatus,
): boolean {
  if (!isLead || !component || !programStatus) return false;

  if (
    programStatus !== ProgramStatus.Complete &&
    programStatus !== ProgramStatus.Live
  ) {
    return false;
  }

  const { status, type } = component;

  return (
    (status === ComponentStatus.Completed ||
      status === ComponentStatus.Ongoing) &&
    type !== ComponentTypeEnum.GroupMeeting &&
    type !== ComponentTypeEnum.Announcement
  );
}
